import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'

import { useApiClientUser } from '~/@api/client/useApiClientUser'
import type {
  IUserVerifyPhoneNumberStartRequest,
  IUserVerifyPhoneNumberVerifyRequest,
} from '~/@api/generated-api-types-user/Api'
import { getApiErrorMessage } from '~/@lib/utils/error.utils'
import { useLocalNotifications } from '~/@mid_ui/local-notifications/useLocalNotifications'

export type VerifyPhoneNumberStartRequest = IUserVerifyPhoneNumberStartRequest
export type VerifyPhoneNumberVerifyRequest = Pick<
  IUserVerifyPhoneNumberVerifyRequest,
  'code'
>

export function usePhoneVerification() {
  const { setNotification } = useLocalNotifications()
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [verifiedPhoneNumber, setVerifiedPhoneNumber] = useState<string | null>(
    null
  )
  const [twoFaCodeExpiresAt, setTwoFaCodeExpiresAt] = useState<Date | null>(
    null
  )
  const [gdpr, setGdpr] = useState<boolean | undefined>(undefined)
  const [marketing, setMarketing] = useState<boolean | undefined>(undefined)
  const client = useApiClientUser()

  const verifyPhoneNumberStartMutation = useMutation({
    mutationFn: async (props: VerifyPhoneNumberStartRequest) => {
      return await client.user
        .onboardingControllerStartPhoneVerification(props, {
          secure: true,
        })
        .then((resp) => resp.data)
    },
    onSuccess: (data, variables) => {
      setVerifiedPhoneNumber(variables.number)
      setTwoFaCodeExpiresAt(data.expiresAt ? new Date(data.expiresAt) : null)
      setIsDialogOpen(true)
    },
  })

  const verifyPhoneNumberVerifyMutation = useMutation({
    mutationFn: async (props: VerifyPhoneNumberVerifyRequest) => {
      if (!verifiedPhoneNumber) {
        setNotification('error', 'Phone number is not verified')
        return
      }

      return await client.user.onboardingControllerVerifyPhoneNumber(
        {
          number: verifiedPhoneNumber,
          code: props.code,
          gdpr,
          marketing,
        },
        { secure: true }
      )
    },
  })

  function handleCloseDialog() {
    setIsDialogOpen(false)
    verifyPhoneNumberStartMutation.reset()
    verifyPhoneNumberVerifyMutation.reset()
  }

  return {
    verifiedPhoneNumber,
    twoFaCodeExpiresAt,

    gdpr,
    setGdpr,
    marketing,
    setMarketing,

    isDialogOpen,
    handleCloseDialog,

    isRequestingVerifyPhoneNumberStart:
      verifyPhoneNumberStartMutation.isPending,
    verifyPhoneNumberStartError:
      getApiErrorMessage(
        verifyPhoneNumberStartMutation.error,
        'Overovací kód sa nepodarilo odoslať.'
      ) ?? undefined,
    verifyPhoneNumberStart: async (
      props: IUserVerifyPhoneNumberStartRequest
    ) => {
      await verifyPhoneNumberStartMutation.mutateAsync(props)
    },

    verifyPhoneNumberVerify: verifyPhoneNumberVerifyMutation.mutate,
    isRequestingVerifyPhoneNumberVerify:
      verifyPhoneNumberVerifyMutation.isPending,
    verifyPhoneNumberVerifyError: getApiErrorMessage(
      verifyPhoneNumberVerifyMutation.error,
      'Telefónne číslo sa nepodarilo overiť.'
    ),
    verifyPhoneNumberReset: () => {
      verifyPhoneNumberVerifyMutation.reset()
    },
  }
}
